/**
 * ============================================
 *  英语常见单词数据（英语单词记忆屋）
 * ============================================
 *  每个单词包含：
 *    - word        拼写
 *    - phoneticUK  英式音标
 *    - phoneticUS  美式音标
 *    - pos         词性（n. 名词 / v. 动词 / adj. 形容词）
 *    - meaning     中文释义
 *    - example     英文例句
 *    - exampleZh   例句中文翻译
 *    - translations 多语言翻译（中文/日语/韩语/法语/德语/西班牙语/俄语）
 */

/** 支持的多语言选项（用于页面语言切换） */
export const LANGUAGE_OPTIONS = [
  { key: 'zh', label: '中文', flag: '🇨🇳' },
  { key: 'ja', label: '日本語', flag: '🇯🇵' },
  { key: 'ko', label: '한국어', flag: '🇰🇷' },
  { key: 'fr', label: 'Français', flag: '🇫🇷' },
  { key: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { key: 'es', label: 'Español', flag: '🇪🇸' },
  { key: 'ru', label: 'Русский', flag: '🇷🇺' }
]

/** 常见单词列表 */
export const COMMON_WORDS = [
  {
    word: 'apple',
    phoneticUK: '/ˈæpl/',
    phoneticUS: '/ˈæpl/',
    pos: 'n.',
    meaning: '苹果',
    example: 'I eat an apple every day.',
    exampleZh: '我每天吃一个苹果。',
    translations: {
      zh: '苹果',
      ja: 'りんご',
      ko: '사과',
      fr: 'pomme',
      de: 'Apfel',
      es: 'manzana',
      ru: 'яблоко'
    }
  },
  {
    word: 'water',
    phoneticUK: '/ˈwɔːtə/',
    phoneticUS: '/ˈwɔːtər/',
    pos: 'n.',
    meaning: '水',
    example: 'Please give me a glass of water.',
    exampleZh: '请给我一杯水。',
    translations: {
      zh: '水',
      ja: '水（みず）',
      ko: '물',
      fr: 'eau',
      de: 'Wasser',
      es: 'agua',
      ru: 'вода'
    }
  },
  {
    word: 'sun',
    phoneticUK: '/sʌn/',
    phoneticUS: '/sʌn/',
    pos: 'n.',
    meaning: '太阳',
    example: 'The sun rises in the east.',
    exampleZh: '太阳从东方升起。',
    translations: {
      zh: '太阳',
      ja: '太陽（たいよう）',
      ko: '태양',
      fr: 'soleil',
      de: 'Sonne',
      es: 'sol',
      ru: 'солнце'
    }
  },
  {
    word: 'moon',
    phoneticUK: '/muːn/',
    phoneticUS: '/muːn/',
    pos: 'n.',
    meaning: '月亮',
    example: 'The moon is bright tonight.',
    exampleZh: '今晚的月亮很亮。',
    translations: {
      zh: '月亮',
      ja: '月（つき）',
      ko: '달',
      fr: 'lune',
      de: 'Mond',
      es: 'luna',
      ru: 'луна'
    }
  },
  {
    word: 'star',
    phoneticUK: '/stɑː/',
    phoneticUS: '/stɑːr/',
    pos: 'n.',
    meaning: '星星',
    example: 'I saw a shooting star last night.',
    exampleZh: '我昨晚看到一颗流星。',
    translations: {
      zh: '星星',
      ja: '星（ほし）',
      ko: '별',
      fr: 'étoile',
      de: 'Stern',
      es: 'estrella',
      ru: 'звезда'
    }
  },
  {
    word: 'book',
    phoneticUK: '/bʊk/',
    phoneticUS: '/bʊk/',
    pos: 'n.',
    meaning: '书',
    example: 'This book is very interesting.',
    exampleZh: '这本书很有趣。',
    translations: {
      zh: '书',
      ja: '本（ほん）',
      ko: '책',
      fr: 'livre',
      de: 'Buch',
      es: 'libro',
      ru: 'книга'
    }
  },
  {
    word: 'school',
    phoneticUK: '/skuːl/',
    phoneticUS: '/skuːl/',
    pos: 'n.',
    meaning: '学校',
    example: 'She goes to school by bus.',
    exampleZh: '她坐公交车去上学。',
    translations: {
      zh: '学校',
      ja: '学校（がっこう）',
      ko: '학교',
      fr: 'école',
      de: 'Schule',
      es: 'escuela',
      ru: 'школа'
    }
  },
  {
    word: 'friend',
    phoneticUK: '/frend/',
    phoneticUS: '/frend/',
    pos: 'n.',
    meaning: '朋友',
    example: 'He is my best friend.',
    exampleZh: '他是我最好的朋友。',
    translations: {
      zh: '朋友',
      ja: '友達（ともだち）',
      ko: '친구',
      fr: 'ami',
      de: 'Freund',
      es: 'amigo',
      ru: 'друг'
    }
  },
  {
    word: 'family',
    phoneticUK: '/ˈfæməli/',
    phoneticUS: '/ˈfæməli/',
    pos: 'n.',
    meaning: '家庭；家人',
    example: 'My family loves me very much.',
    exampleZh: '我的家人非常爱我。',
    translations: {
      zh: '家庭；家人',
      ja: '家族（かぞく）',
      ko: '가족',
      fr: 'famille',
      de: 'Familie',
      es: 'familia',
      ru: 'семья'
    }
  },
  {
    word: 'love',
    phoneticUK: '/lʌv/',
    phoneticUS: '/lʌv/',
    pos: 'n.',
    meaning: '爱',
    example: 'Love makes the world warm.',
    exampleZh: '爱让世界变得温暖。',
    translations: {
      zh: '爱',
      ja: '愛（あい）',
      ko: '사랑',
      fr: 'amour',
      de: 'Liebe',
      es: 'amor',
      ru: 'любовь'
    }
  },
  {
    word: 'happy',
    phoneticUK: '/ˈhæpi/',
    phoneticUS: '/ˈhæpi/',
    pos: 'adj.',
    meaning: '快乐的；幸福的',
    example: 'I am happy today.',
    exampleZh: '我今天很开心。',
    translations: {
      zh: '快乐的；幸福的',
      ja: '幸せな',
      ko: '행복한',
      fr: 'heureux',
      de: 'glücklich',
      es: 'feliz',
      ru: 'счастливый'
    }
  },
  {
    word: 'beautiful',
    phoneticUK: '/ˈbjuːtɪfəl/',
    phoneticUS: '/ˈbjuːtəfəl/',
    pos: 'adj.',
    meaning: '美丽的',
    example: 'What a beautiful flower!',
    exampleZh: '多么美丽的花啊！',
    translations: {
      zh: '美丽的',
      ja: '美しい',
      ko: '아름다운',
      fr: 'beau',
      de: 'schön',
      es: 'hermoso',
      ru: 'красивый'
    }
  },
  {
    word: 'dream',
    phoneticUK: '/driːm/',
    phoneticUS: '/driːm/',
    pos: 'n.',
    meaning: '梦想；梦',
    example: 'Follow your dream bravely.',
    exampleZh: '勇敢追逐你的梦想。',
    translations: {
      zh: '梦想；梦',
      ja: '夢（ゆめ）',
      ko: '꿈',
      fr: 'rêve',
      de: 'Traum',
      es: 'sueño',
      ru: 'мечта'
    }
  },
  {
    word: 'music',
    phoneticUK: '/ˈmjuːzɪk/',
    phoneticUS: '/ˈmjuːzɪk/',
    pos: 'n.',
    meaning: '音乐',
    example: 'I like listening to music.',
    exampleZh: '我喜欢听音乐。',
    translations: {
      zh: '音乐',
      ja: '音楽（おんがく）',
      ko: '음악',
      fr: 'musique',
      de: 'Musik',
      es: 'música',
      ru: 'музыка'
    }
  },
  {
    word: 'flower',
    phoneticUK: '/ˈflaʊə/',
    phoneticUS: '/ˈflaʊər/',
    pos: 'n.',
    meaning: '花',
    example: 'The flower is blooming.',
    exampleZh: '这朵花正在绽放。',
    translations: {
      zh: '花',
      ja: '花（はな）',
      ko: '꽃',
      fr: 'fleur',
      de: 'Blume',
      es: 'flor',
      ru: 'цветок'
    }
  },
  {
    word: 'sky',
    phoneticUK: '/skaɪ/',
    phoneticUS: '/skaɪ/',
    pos: 'n.',
    meaning: '天空',
    example: 'The sky is so blue today.',
    exampleZh: '今天的天好蓝。',
    translations: {
      zh: '天空',
      ja: '空（そら）',
      ko: '하늘',
      fr: 'ciel',
      de: 'Himmel',
      es: 'cielo',
      ru: 'небо'
    }
  },
  {
    word: 'sea',
    phoneticUK: '/siː/',
    phoneticUS: '/siː/',
    pos: 'n.',
    meaning: '大海',
    example: 'We swam in the sea.',
    exampleZh: '我们在海里游泳。',
    translations: {
      zh: '大海',
      ja: '海（うみ）',
      ko: '바다',
      fr: 'mer',
      de: 'Meer',
      es: 'mar',
      ru: 'море'
    }
  },
  {
    word: 'time',
    phoneticUK: '/taɪm/',
    phoneticUS: '/taɪm/',
    pos: 'n.',
    meaning: '时间',
    example: 'Time waits for no one.',
    exampleZh: '时间不等人。',
    translations: {
      zh: '时间',
      ja: '時間（じかん）',
      ko: '시간',
      fr: 'temps',
      de: 'Zeit',
      es: 'tiempo',
      ru: 'время'
    }
  },
  {
    word: 'life',
    phoneticUK: '/laɪf/',
    phoneticUS: '/laɪf/',
    pos: 'n.',
    meaning: '生活；人生',
    example: 'Life is full of hope.',
    exampleZh: '生活充满希望。',
    translations: {
      zh: '生活；人生',
      ja: '人生（じんせい）',
      ko: '인생',
      fr: 'vie',
      de: 'Leben',
      es: 'vida',
      ru: 'жизнь'
    }
  },
  {
    word: 'hope',
    phoneticUK: '/həʊp/',
    phoneticUS: '/hoʊp/',
    pos: 'n.',
    meaning: '希望',
    example: 'Never give up hope.',
    exampleZh: '永远不要放弃希望。',
    translations: {
      zh: '希望',
      ja: '希望（きぼう）',
      ko: '희망',
      fr: 'espoir',
      de: 'Hoffnung',
      es: 'esperanza',
      ru: 'надежда'
    }
  },
  {
    word: 'smile',
    phoneticUK: '/smaɪl/',
    phoneticUS: '/smaɪl/',
    pos: 'n.',
    meaning: '微笑',
    example: 'Her smile is so sweet.',
    exampleZh: '她的微笑很甜。',
    translations: {
      zh: '微笑',
      ja: '微笑み（ほほえみ）',
      ko: '미소',
      fr: 'sourire',
      de: 'Lächeln',
      es: 'sonrisa',
      ru: 'улыбка'
    }
  },
  {
    word: 'rain',
    phoneticUK: '/reɪn/',
    phoneticUS: '/reɪn/',
    pos: 'n.',
    meaning: '雨',
    example: 'The rain is falling softly.',
    exampleZh: '雨正轻轻地落下。',
    translations: {
      zh: '雨',
      ja: '雨（あめ）',
      ko: '비',
      fr: 'pluie',
      de: 'Regen',
      es: 'lluvia',
      ru: 'дождь'
    }
  },
  {
    word: 'snow',
    phoneticUK: '/snəʊ/',
    phoneticUS: '/snoʊ/',
    pos: 'n.',
    meaning: '雪',
    example: 'Snow covers the whole town.',
    exampleZh: '雪覆盖了整座小镇。',
    translations: {
      zh: '雪',
      ja: '雪（ゆき）',
      ko: '눈',
      fr: 'neige',
      de: 'Schnee',
      es: 'nieve',
      ru: 'снег'
    }
  },
  {
    word: 'light',
    phoneticUK: '/laɪt/',
    phoneticUS: '/laɪt/',
    pos: 'n.',
    meaning: '光；光线',
    example: 'The light is shining through the window.',
    exampleZh: '阳光透过窗户照进来。',
    translations: {
      zh: '光；光线',
      ja: '光（ひかり）',
      ko: '빛',
      fr: 'lumière',
      de: 'Licht',
      es: 'luz',
      ru: 'свет'
    }
  },
  {
    word: 'night',
    phoneticUK: '/naɪt/',
    phoneticUS: '/naɪt/',
    pos: 'n.',
    meaning: '夜晚',
    example: 'The night is quiet and peaceful.',
    exampleZh: '夜晚安静而祥和。',
    translations: {
      zh: '夜晚',
      ja: '夜（よる）',
      ko: '밤',
      fr: 'nuit',
      de: 'Nacht',
      es: 'noche',
      ru: 'ночь'
    }
  },
  {
    word: 'morning',
    phoneticUK: '/ˈmɔːnɪŋ/',
    phoneticUS: '/ˈmɔːrnɪŋ/',
    pos: 'n.',
    meaning: '早晨',
    example: 'I do exercise every morning.',
    exampleZh: '我每天早上锻炼。',
    translations: {
      zh: '早晨',
      ja: '朝（あさ）',
      ko: '아침',
      fr: 'matin',
      de: 'Morgen',
      es: 'mañana',
      ru: 'утро'
    }
  },
  {
    word: 'cat',
    phoneticUK: '/kæt/',
    phoneticUS: '/kæt/',
    pos: 'n.',
    meaning: '猫',
    example: 'The cat is sleeping on the sofa.',
    exampleZh: '猫在沙发上睡觉。',
    translations: {
      zh: '猫',
      ja: '猫（ねこ）',
      ko: '고양이',
      fr: 'chat',
      de: 'Katze',
      es: 'gato',
      ru: 'кошка'
    }
  },
  {
    word: 'dog',
    phoneticUK: '/dɒɡ/',
    phoneticUS: '/dɔːɡ/',
    pos: 'n.',
    meaning: '狗',
    example: 'The dog is running in the park.',
    exampleZh: '狗在公园里奔跑。',
    translations: {
      zh: '狗',
      ja: '犬（いぬ）',
      ko: '개',
      fr: 'chien',
      de: 'Hund',
      es: 'perro',
      ru: 'собака'
    }
  },
  {
    word: 'bird',
    phoneticUK: '/bɜːd/',
    phoneticUS: '/bɜːrd/',
    pos: 'n.',
    meaning: '鸟',
    example: 'The bird sings in the tree.',
    exampleZh: '鸟儿在树上唱歌。',
    translations: {
      zh: '鸟',
      ja: '鳥（とり）',
      ko: '새',
      fr: 'oiseau',
      de: 'Vogel',
      es: 'pájaro',
      ru: 'птица'
    }
  },
  {
    word: 'tree',
    phoneticUK: '/triː/',
    phoneticUS: '/triː/',
    pos: 'n.',
    meaning: '树',
    example: 'We planted a tree in the garden.',
    exampleZh: '我们在花园里种了一棵树。',
    translations: {
      zh: '树',
      ja: '木（き）',
      ko: '나무',
      fr: 'arbre',
      de: 'Baum',
      es: 'árbol',
      ru: 'дерево'
    }
  },
  {
    word: 'mountain',
    phoneticUK: '/ˈmaʊntən/',
    phoneticUS: '/ˈmaʊntən/',
    pos: 'n.',
    meaning: '山',
    example: 'We climbed the mountain yesterday.',
    exampleZh: '我们昨天爬了那座山。',
    translations: {
      zh: '山',
      ja: '山（やま）',
      ko: '산',
      fr: 'montagne',
      de: 'Berg',
      es: 'montaña',
      ru: 'гора'
    }
  },
  {
    word: 'river',
    phoneticUK: '/ˈrɪvə/',
    phoneticUS: '/ˈrɪvər/',
    pos: 'n.',
    meaning: '河流',
    example: 'The river flows into the sea.',
    exampleZh: '这条河流入大海。',
    translations: {
      zh: '河流',
      ja: '川（かわ）',
      ko: '강',
      fr: 'rivière',
      de: 'Fluss',
      es: 'río',
      ru: 'река'
    }
  },
  {
    word: 'city',
    phoneticUK: '/ˈsɪti/',
    phoneticUS: '/ˈsɪti/',
    pos: 'n.',
    meaning: '城市',
    example: 'This city is very lively.',
    exampleZh: '这座城市非常热闹。',
    translations: {
      zh: '城市',
      ja: '都市（とし）',
      ko: '도시',
      fr: 'ville',
      de: 'Stadt',
      es: 'ciudad',
      ru: 'город'
    }
  },
  {
    word: 'world',
    phoneticUK: '/wɜːld/',
    phoneticUS: '/wɜːrld/',
    pos: 'n.',
    meaning: '世界',
    example: 'I want to travel around the world.',
    exampleZh: '我想环游世界。',
    translations: {
      zh: '世界',
      ja: '世界（せかい）',
      ko: '세계',
      fr: 'monde',
      de: 'Welt',
      es: 'mundo',
      ru: 'мир'
    }
  },
  {
    word: 'travel',
    phoneticUK: '/ˈtrævl/',
    phoneticUS: '/ˈtrævl/',
    pos: 'v.',
    meaning: '旅行',
    example: 'Travel broadens our horizons.',
    exampleZh: '旅行开阔我们的视野。',
    translations: {
      zh: '旅行',
      ja: '旅行（りょこう）',
      ko: '여행',
      fr: 'voyage',
      de: 'Reise',
      es: 'viaje',
      ru: 'путешествие'
    }
  },
  {
    word: 'learn',
    phoneticUK: '/lɜːn/',
    phoneticUS: '/lɜːrn/',
    pos: 'v.',
    meaning: '学习',
    example: 'We should learn new things every day.',
    exampleZh: '我们应该每天学习新东西。',
    translations: {
      zh: '学习',
      ja: '学ぶ',
      ko: '배우다',
      fr: 'apprendre',
      de: 'lernen',
      es: 'aprender',
      ru: 'учить'
    }
  },
  {
    word: 'read',
    phoneticUK: '/riːd/',
    phoneticUS: '/riːd/',
    pos: 'v.',
    meaning: '阅读',
    example: 'I like to read before sleeping.',
    exampleZh: '我喜欢睡前阅读。',
    translations: {
      zh: '阅读',
      ja: '読む',
      ko: '읽다',
      fr: 'lire',
      de: 'lesen',
      es: 'leer',
      ru: 'читать'
    }
  },
  {
    word: 'write',
    phoneticUK: '/raɪt/',
    phoneticUS: '/raɪt/',
    pos: 'v.',
    meaning: '写作；写',
    example: 'She writes a letter to her friend.',
    exampleZh: '她给朋友写了一封信。',
    translations: {
      zh: '写作；写',
      ja: '書く',
      ko: '쓰다',
      fr: 'écrire',
      de: 'schreiben',
      es: 'escribir',
      ru: 'писать'
    }
  },
  {
    word: 'speak',
    phoneticUK: '/spiːk/',
    phoneticUS: '/spiːk/',
    pos: 'v.',
    meaning: '说；讲话',
    example: 'Can you speak English?',
    exampleZh: '你会说英语吗？',
    translations: {
      zh: '说；讲话',
      ja: '話す',
      ko: '말하다',
      fr: 'parler',
      de: 'sprechen',
      es: 'hablar',
      ru: 'говорить'
    }
  },
  {
    word: 'think',
    phoneticUK: '/θɪŋk/',
    phoneticUS: '/θɪŋk/',
    pos: 'v.',
    meaning: '思考；认为',
    example: 'I think this idea is great.',
    exampleZh: '我觉得这个主意很棒。',
    translations: {
      zh: '思考；认为',
      ja: '考える',
      ko: '생각하다',
      fr: 'penser',
      de: 'denken',
      es: 'pensar',
      ru: 'думать'
    }
  }
]
